import { isPigmentDocument } from "./document.js";
import { PIGMENT_CAPABILITY_MANIFEST_SCHEMA, PIGMENT_DOCUMENT_VALIDATION_SCHEMA } from "./manifest.js";
/**
 * Environment-neutral, content-closed frontend publication transported by a
 * release/deployment owner such as Nest. V2 deliberately contains no release,
 * deployment, environment, or rollback identity.
 */
export const PIGMENT_PUBLICATION_ENVELOPE_SCHEMA = "pigment.publication-envelope.v2";
/** Canonical root transport entry; deployable output files remain byte-closed separately. */
export const PIGMENT_PUBLICATION_ENVELOPE_FILE = "pigment-publication.json";
const SHA256 = /^sha256:[a-f0-9]{64}$/u;
const CONTROL = /[\u0000-\u001f\u007f]/u;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasOnlyKeys(value, allowed, path, issues) {
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key))
            issues.push({ path: `${path}.${key}`, code: "unknown_key", message: "Unknown publication key." });
    }
}
function issue(issues, path, code, message) {
    issues.push({ path, code, message });
}
function isText(value) {
    return typeof value === "string" && value.length > 0 && value === value.trim() && !CONTROL.test(value);
}
function isSha256(value) {
    return typeof value === "string" && SHA256.test(value);
}
function isSize(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
/** A normalized portable POSIX path, deliberately not a URL or host pathname. */
export function isPigmentPublicationPath(value) {
    if (!isText(value) || value !== value.normalize("NFC") || value.includes("\\") || value.startsWith("/") || value.includes("?") || value.includes("#") || value.includes("%"))
        return false;
    const segments = value.split("/");
    return segments.length > 0
        && segments.every((segment) => segment.length > 0 && segment !== "." && segment !== ".." && !CONTROL.test(segment));
}
function isRoutePath(value) {
    if (!isText(value) || value !== value.normalize("NFC") || !value.startsWith("/") || value.includes("\\") || value.includes("?") || value.includes("#") || value.includes("%") || CONTROL.test(value))
        return false;
    const segments = value.slice(1).split("/");
    return segments.every((segment) => segment !== "." && segment !== "..");
}
function isMediaType(value) {
    return isText(value) && /^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*(?:;[a-z0-9!#$&^_.+-]+=[a-z0-9!#$&^_.+-]+)*$/u.test(value);
}
function isPortableJson(value, seen = new Set()) {
    if (value === null || typeof value === "string" || typeof value === "boolean")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (typeof value !== "object" || seen.has(value))
        return false;
    if (Array.isArray(value)) {
        seen.add(value);
        const valid = value.every((entry) => isPortableJson(entry, seen));
        seen.delete(value);
        return valid;
    }
    if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)
        return false;
    seen.add(value);
    const valid = Object.keys(value).every((key) => isText(key) && isPortableJson(value[key], seen));
    seen.delete(value);
    return valid;
}
/** Locale-independent JSON for values that can be carried across runtimes. */
export function canonicalPigmentJson(value) {
    if (!isPortableJson(value))
        throw new TypeError("Pigment canonical JSON accepts only finite, plain JSON values.");
    if (Array.isArray(value))
        return `[${value.map(canonicalPigmentJson).join(",")}]`;
    if (value !== null && typeof value === "object") {
        return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalPigmentJson(value[key])}`).join(",")}}`;
    }
    return JSON.stringify(value);
}
export function canonicalPigmentJsonBytes(value) {
    return new TextEncoder().encode(canonicalPigmentJson(value));
}
/** Browser and Node WebCrypto digest; callers retain ownership of filesystem I/O. */
export async function sha256PigmentBytes(value) {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle)
        throw new Error("WebCrypto SubtleCrypto is required to calculate Pigment SHA-256.");
    const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
    // Copy into an ordinary ArrayBuffer: WebCrypto intentionally refuses a
    // SharedArrayBuffer-backed view, while callers may supply either view type.
    const digest = new Uint8Array(await subtle.digest("SHA-256", Uint8Array.from(bytes).buffer));
    return `sha256:${Array.from(digest, (part) => part.toString(16).padStart(2, "0")).join("")}`;
}
export async function sha256CanonicalPigmentJson(value) {
    return sha256PigmentBytes(canonicalPigmentJsonBytes(value));
}
/** The deterministic subset which identifies deploy-affecting publication content. */
export function pigmentPublicationContent(body) {
    return {
        document: body.document,
        library: body.library,
        renderer: body.renderer,
        output: body.output,
        assets: body.assets,
        dataDependencies: body.dataDependencies,
        cache: body.cache,
    };
}
function checkFile(value, path, issues, actual = false) {
    const start = issues.length;
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "File descriptor must be an object.");
        return false;
    }
    hasOnlyKeys(value, actual ? ["path", "mediaType", "sizeBytes", "sha256", "type"] : ["path", "mediaType", "sizeBytes", "sha256"], path, issues);
    if (!isPigmentPublicationPath(value.path))
        issue(issues, `${path}.path`, "invalid_value", "File path must be a normalized relative POSIX path.");
    if (!isMediaType(value.mediaType))
        issue(issues, `${path}.mediaType`, "invalid_value", "File media type must be lowercase MIME.");
    if (!isSize(value.sizeBytes))
        issue(issues, `${path}.sizeBytes`, "invalid_value", "File size must be a safe non-negative integer.");
    if (!isSha256(value.sha256))
        issue(issues, `${path}.sha256`, "invalid_value", "File digest must be sha256: plus lowercase hex.");
    if (actual && value.type !== "file")
        issue(issues, `${path}.type`, "invalid_value", "Actual inventory accepts regular files only.");
    return issues.length === start;
}
function unique(values, path, issues, label) {
    const exact = new Set();
    const folded = new Set();
    for (const value of values) {
        const key = value.normalize("NFC").toLowerCase();
        if (exact.has(value))
            issue(issues, path, "duplicate", `Duplicate ${label}.`);
        if (folded.has(key))
            issue(issues, path, "duplicate", `Case- or normalization-colliding ${label}.`);
        exact.add(value);
        folded.add(key);
    }
}
function requireRecord(value, path, allowed, issues) {
    if (!isRecord(value)) {
        issue(issues, path, "invalid_type", "Value must be an object.");
        return null;
    }
    hasOnlyKeys(value, allowed, path, issues);
    return value;
}
function requireText(record, key, path, issues) {
    if (!isText(record[key]))
        issue(issues, `${path}.${key}`, "invalid_value", "Value must be non-empty trimmed text.");
}
function requireSha(record, key, path, issues) {
    if (!isSha256(record[key]))
        issue(issues, `${path}.${key}`, "invalid_value", "Value must be a SHA-256 digest.");
}
function requirePath(record, key, path, issues) {
    if (!isPigmentPublicationPath(record[key]))
        issue(issues, `${path}.${key}`, "invalid_value", "Value must be a normalized relative POSIX path.");
}
function validateBody(value, issues) {
    const body = requireRecord(value, "body", ["publication", "document", "library", "renderer", "output", "assets", "dataDependencies", "cache", "validation", "provenance"], issues);
    if (!body)
        return false;
    const publication = requireRecord(body.publication, "body.publication", ["id", "page", "documentRevision"], issues);
    if (publication) {
        requireText(publication, "page", "body.publication", issues);
        if (!isSize(publication.documentRevision))
            issue(issues, "body.publication.documentRevision", "invalid_value", "Document revision must be a safe non-negative integer.");
        if (!isText(publication.id))
            issue(issues, "body.publication.id", "invalid_value", "Publication ID must be trimmed text.");
    }
    const document = requireRecord(body.document, "body.document", ["schema", "value", "sha256"], issues);
    if (document) {
        if (document.schema !== "pigment.document.v1")
            issue(issues, "body.document.schema", "invalid_value", "Only pigment.document.v1 is portable.");
        if (!isPigmentDocument(document.value))
            issue(issues, "body.document.value", "invalid_value", "Document must be an exact portable PigmentDocument.");
        requireSha(document, "sha256", "body.document", issues);
    }
    const library = requireRecord(body.library, "body.library", ["manifestSchema", "manifestVersion", "manifestPath", "manifestSha256", "runtimePath", "runtimeSha256"], issues);
    if (library) {
        if (library.manifestSchema !== PIGMENT_CAPABILITY_MANIFEST_SCHEMA)
            issue(issues, "body.library.manifestSchema", "invalid_value", "Library manifest must use the P3 schema.");
        requireText(library, "manifestVersion", "body.library", issues);
        requirePath(library, "manifestPath", "body.library", issues);
        requireSha(library, "manifestSha256", "body.library", issues);
        requirePath(library, "runtimePath", "body.library", issues);
        requireSha(library, "runtimeSha256", "body.library", issues);
    }
    const renderer = requireRecord(body.renderer, "body.renderer", ["name", "version", "abi", "entrypoint", "entrypointSha256"], issues);
    if (renderer) {
        for (const key of ["name", "version", "abi"])
            requireText(renderer, key, "body.renderer", issues);
        requirePath(renderer, "entrypoint", "body.renderer", issues);
        requireSha(renderer, "entrypointSha256", "body.renderer", issues);
    }
    const output = requireRecord(body.output, "body.output", ["kind", "routes", "files"], issues);
    const files = [];
    if (output) {
        if (output.kind !== "static-node")
            issue(issues, "body.output.kind", "invalid_value", "Only static-node output is accepted by v2.");
        if (!Array.isArray(output.files))
            issue(issues, "body.output.files", "invalid_type", "Output files must be an array.");
        else
            output.files.forEach((file, index) => {
                const before = issues.length;
                checkFile(file, `body.output.files[${index}]`, issues);
                if (before === issues.length) {
                    const accepted = file;
                    if (accepted.path === PIGMENT_PUBLICATION_ENVELOPE_FILE
                        || accepted.path.startsWith(`${PIGMENT_PUBLICATION_ENVELOPE_FILE}/`)) {
                        issue(issues, `body.output.files[${index}].path`, "invalid_value", "Output files must not collide with the root publication envelope entry.");
                    }
                    else
                        files.push(accepted);
                }
            });
        if (!Array.isArray(output.routes))
            issue(issues, "body.output.routes", "invalid_type", "Output routes must be an array.");
        else
            output.routes.forEach((route, index) => {
                const entry = requireRecord(route, `body.output.routes[${index}]`, ["path", "file", "mediaType"], issues);
                if (!entry)
                    return;
                if (!isRoutePath(entry.path))
                    issue(issues, `body.output.routes[${index}].path`, "invalid_value", "Route path must be normalized and absolute.");
                requirePath(entry, "file", `body.output.routes[${index}]`, issues);
                if (!isMediaType(entry.mediaType))
                    issue(issues, `body.output.routes[${index}].mediaType`, "invalid_value", "Route media type must be lowercase MIME.");
            });
    }
    unique(files.map((file) => file.path), "body.output.files", issues, "file path");
    const byPath = new Map(files.map((file) => [file.path, file]));
    if (output && Array.isArray(output.routes)) {
        unique(output.routes.filter(isRecord).map((route) => String(route.path)), "body.output.routes", issues, "route path");
        for (const [index, route] of output.routes.entries())
            if (isRecord(route)) {
                const declared = byPath.get(route.file);
                if (!declared)
                    issue(issues, `body.output.routes[${index}].file`, "missing_file", "Route file is not in output.files.");
                else if (declared.mediaType !== route.mediaType)
                    issue(issues, `body.output.routes[${index}].mediaType`, "mismatch", "Route media type must match its output file.");
            }
    }
    const assets = body.assets;
    if (!Array.isArray(assets))
        issue(issues, "body.assets", "invalid_type", "Assets must be an array.");
    else
        assets.forEach((asset, index) => {
            const entry = requireRecord(asset, `body.assets[${index}]`, ["sourceReference", "outputPath", "mediaType", "sizeBytes", "sha256"], issues);
            if (!entry)
                return;
            requireText(entry, "sourceReference", `body.assets[${index}]`, issues);
            requirePath(entry, "outputPath", `body.assets[${index}]`, issues);
            if (!isMediaType(entry.mediaType))
                issue(issues, `body.assets[${index}].mediaType`, "invalid_value", "Asset media type must be lowercase MIME.");
            if (!isSize(entry.sizeBytes))
                issue(issues, `body.assets[${index}].sizeBytes`, "invalid_value", "Asset size must be non-negative integer.");
            requireSha(entry, "sha256", `body.assets[${index}]`, issues);
            const file = byPath.get(entry.outputPath);
            if (!file)
                issue(issues, `body.assets[${index}].outputPath`, "missing_file", "Asset output must be in output.files.");
            else if (file.mediaType !== entry.mediaType || file.sizeBytes !== entry.sizeBytes || file.sha256 !== entry.sha256)
                issue(issues, `body.assets[${index}]`, "mismatch", "Asset descriptor must equal its output file.");
        });
    if (Array.isArray(assets)) {
        const records = assets.filter(isRecord);
        unique(records.map((asset) => String(asset.sourceReference)), "body.assets", issues, "asset source reference");
        unique(records.map((asset) => String(asset.outputPath)), "body.assets", issues, "asset output path");
    }
    const dependencies = body.dataDependencies;
    if (!Array.isArray(dependencies))
        issue(issues, "body.dataDependencies", "invalid_type", "Data dependencies must be an array.");
    else
        dependencies.forEach((dependency, index) => {
            const entry = requireRecord(dependency, `body.dataDependencies[${index}]`, ["capability", "mode", "classification", "schemaSha256", "valuePath", "valueSha256"], issues);
            if (!entry)
                return;
            requireText(entry, "capability", `body.dataDependencies[${index}]`, issues);
            if (entry.mode !== "frozen")
                issue(issues, `body.dataDependencies[${index}].mode`, "invalid_value", "Only frozen data is portable in v2.");
            if (entry.classification !== "public")
                issue(issues, `body.dataDependencies[${index}].classification`, "invalid_value", "Only explicitly public data is portable in v2.");
            requireSha(entry, "schemaSha256", `body.dataDependencies[${index}]`, issues);
            requirePath(entry, "valuePath", `body.dataDependencies[${index}]`, issues);
            requireSha(entry, "valueSha256", `body.dataDependencies[${index}]`, issues);
            const file = byPath.get(entry.valuePath);
            if (!file)
                issue(issues, `body.dataDependencies[${index}].valuePath`, "missing_file", "Frozen data value must be in output.files.");
            else if (file.sha256 !== entry.valueSha256)
                issue(issues, `body.dataDependencies[${index}].valueSha256`, "mismatch", "Frozen data digest must match its output file.");
        });
    if (Array.isArray(dependencies)) {
        const records = dependencies.filter(isRecord);
        unique(records.map((dependency) => String(dependency.capability)), "body.dataDependencies", issues, "data capability");
        unique(records.map((dependency) => String(dependency.valuePath)), "body.dataDependencies", issues, "frozen data value path");
    }
    const cache = requireRecord(body.cache, "body.cache", ["routes", "immutablePrefixes", "invalidation"], issues);
    if (cache) {
        if (!Array.isArray(cache.routes))
            issue(issues, "body.cache.routes", "invalid_type", "Cache routes must be an array.");
        else
            cache.routes.forEach((route, index) => { const entry = requireRecord(route, `body.cache.routes[${index}]`, ["path", "cacheControl"], issues); if (entry) {
                if (!isRoutePath(entry.path))
                    issue(issues, `body.cache.routes[${index}].path`, "invalid_value", "Cache route must be normalized.");
                requireText(entry, "cacheControl", `body.cache.routes[${index}]`, issues);
            } });
        if (!Array.isArray(cache.immutablePrefixes) || !cache.immutablePrefixes.every(isRoutePath))
            issue(issues, "body.cache.immutablePrefixes", "invalid_value", "Immutable prefixes must be normalized absolute routes.");
        if (Array.isArray(cache.routes))
            unique(cache.routes.filter(isRecord).map((route) => String(route.path)), "body.cache.routes", issues, "cache route");
        if (Array.isArray(cache.immutablePrefixes))
            unique(cache.immutablePrefixes.filter((prefix) => typeof prefix === "string"), "body.cache.immutablePrefixes", issues, "immutable prefix");
        const invalidation = requireRecord(cache.invalidation, "body.cache.invalidation", ["mode"], issues);
        if (invalidation && invalidation.mode !== "content-hash")
            issue(issues, "body.cache.invalidation.mode", "invalid_value", "V2 invalidation is content-addressed.");
        if (output && Array.isArray(output.routes) && Array.isArray(cache.routes)) {
            const outputRoutes = output.routes.filter(isRecord).map((route) => route.path).filter(isRoutePath).sort();
            const cacheRoutes = cache.routes.filter(isRecord).map((route) => route.path).filter(isRoutePath).sort();
            if (outputRoutes.length !== output.routes.length || cacheRoutes.length !== cache.routes.length || canonicalPigmentJson(outputRoutes) !== canonicalPigmentJson(cacheRoutes))
                issue(issues, "body.cache.routes", "mismatch", "Cache routes must cover exactly the output routes.");
        }
    }
    const validation = requireRecord(body.validation, "body.validation", ["document", "inspection"], issues);
    if (validation) {
        const documentEvidence = requireRecord(validation.document, "body.validation.document", ["schema", "reportPath", "sha256", "ok"], issues);
        if (documentEvidence) {
            if (documentEvidence.schema !== PIGMENT_DOCUMENT_VALIDATION_SCHEMA || documentEvidence.ok !== true)
                issue(issues, "body.validation.document", "invalid_value", "Document evidence must be green P4 validation.");
            requirePath(documentEvidence, "reportPath", "body.validation.document", issues);
            requireSha(documentEvidence, "sha256", "body.validation.document", issues);
            const file = byPath.get(documentEvidence.reportPath);
            if (!file)
                issue(issues, "body.validation.document.reportPath", "missing_file", "Document report must be in output.files.");
            else if (file.sha256 !== documentEvidence.sha256)
                issue(issues, "body.validation.document.sha256", "mismatch", "Document report digest must match output file.");
        }
        const inspection = requireRecord(validation.inspection, "body.validation.inspection", ["schema", "reportPath", "sha256", "artifactHash", "ok"], issues);
        if (inspection) {
            if (inspection.schema !== "ok.pigment.inspection.v1" || inspection.ok !== true)
                issue(issues, "body.validation.inspection", "invalid_value", "Inspection evidence must be green.");
            requirePath(inspection, "reportPath", "body.validation.inspection", issues);
            requireSha(inspection, "sha256", "body.validation.inspection", issues);
            requireSha(inspection, "artifactHash", "body.validation.inspection", issues);
            const file = byPath.get(inspection.reportPath);
            if (!file)
                issue(issues, "body.validation.inspection.reportPath", "missing_file", "Inspection report must be in output.files.");
            else if (file.sha256 !== inspection.sha256)
                issue(issues, "body.validation.inspection.sha256", "mismatch", "Inspection report digest must match output file.");
        }
    }
    const provenance = requireRecord(body.provenance, "body.provenance", ["sourceRepoSlug", "sourceBranch", "sourceSha", "previewId", "previewArtifactHash", "baseRevision", "baseHash", "candidateHash", "rendererBuildId"], issues);
    if (provenance) {
        for (const key of ["sourceRepoSlug", "sourceBranch", "rendererBuildId"])
            requireText(provenance, key, "body.provenance", issues);
        if (typeof provenance.sourceSha !== "string" || !/^(?:[a-f0-9]{40}|[a-f0-9]{64})$/u.test(provenance.sourceSha))
            issue(issues, "body.provenance.sourceSha", "invalid_value", "Source SHA must be lowercase 40- or 64-hex.");
        if (typeof provenance.previewId !== "string" || !/^pvp_[a-f0-9]{64}$/u.test(provenance.previewId))
            issue(issues, "body.provenance.previewId", "invalid_value", "Preview ID must be pvp_ plus lowercase 64-hex.");
        for (const key of ["previewArtifactHash", "baseHash", "candidateHash"])
            requireSha(provenance, key, "body.provenance", issues);
        if (!isSize(provenance.baseRevision))
            issue(issues, "body.provenance.baseRevision", "invalid_value", "Base revision must be a non-negative integer.");
    }
    const references = [["body.library.manifestPath", library?.manifestPath, library?.manifestSha256], ["body.library.runtimePath", library?.runtimePath, library?.runtimeSha256], ["body.renderer.entrypoint", renderer?.entrypoint, renderer?.entrypointSha256]];
    for (const [path, filePath, hash] of references) {
        const file = byPath.get(filePath);
        if (!file)
            issue(issues, path, "missing_file", "Referenced file must be in output.files.");
        else if (file.sha256 !== hash)
            issue(issues, path, "mismatch", "Referenced file digest must match output file.");
    }
    if (validation && provenance && isRecord(validation.inspection) && validation.inspection.artifactHash !== provenance.previewArtifactHash)
        issue(issues, "body.validation.inspection.artifactHash", "mismatch", "Inspection evidence must bind the exact P4 preview artifact hash.");
    if (publication && isText(publication.id) && provenance && isSha256(provenance.candidateHash) && !publication.id.startsWith("pub_"))
        issue(issues, "body.publication.id", "invalid_value", "Publication ID must be content-addressed with pub_.");
    return issues.length === 0;
}
/**
 * Strict structural and byte-inventory validation. `actualFiles` is exactly
 * `output.files`: the root `pigment-publication.json` envelope is deliberately
 * outside this inventory, so passing it here is an undeclared-file refusal.
 * Digest calculation is asynchronous below.
 */
export function validatePigmentPublicationEnvelope(value, actualFiles) {
    const issues = [];
    const envelope = requireRecord(value, "$", ["schema", "body", "contentHash", "envelopeHash"], issues);
    if (!envelope)
        return { ok: false, issues };
    if (envelope.schema !== PIGMENT_PUBLICATION_ENVELOPE_SCHEMA)
        issue(issues, "$.schema", "invalid_value", "Unsupported publication envelope schema.");
    validateBody(envelope.body, issues);
    if (!isSha256(envelope.contentHash))
        issue(issues, "$.contentHash", "invalid_value", "Content hash must be SHA-256.");
    if (!isSha256(envelope.envelopeHash))
        issue(issues, "$.envelopeHash", "invalid_value", "Envelope hash must be SHA-256.");
    if (isRecord(envelope.body) && isRecord(envelope.body.publication) && isSha256(envelope.contentHash) && envelope.body.publication.id !== `pub_${envelope.contentHash.slice("sha256:".length)}`)
        issue(issues, "$.body.publication.id", "mismatch", "Publication ID must equal pub_ plus content hash.");
    if (actualFiles !== undefined && isRecord(envelope.body) && isRecord(envelope.body.output) && Array.isArray(envelope.body.output.files)) {
        const actual = new Map();
        actualFiles.forEach((file, index) => { const before = issues.length; checkFile(file, `actualFiles[${index}]`, issues, true); if (before === issues.length)
            actual.set(file.path, file); });
        unique(actualFiles.map((file) => file.path), "actualFiles", issues, "actual file path");
        const declared = new Map(envelope.body.output.files.map((file) => [file.path, file]));
        for (const [path, expected] of declared) {
            const actualFile = actual.get(path);
            if (!actualFile)
                issue(issues, `actualFiles.${path}`, "missing_file", "Declared output file is absent from actual inventory.");
            else if (actualFile.mediaType !== expected.mediaType || actualFile.sizeBytes !== expected.sizeBytes || actualFile.sha256 !== expected.sha256)
                issue(issues, `actualFiles.${path}`, "mismatch", "Actual descriptor differs from declared output file.");
        }
        for (const path of actual.keys())
            if (!declared.has(path))
                issue(issues, `actualFiles.${path}`, "unexpected_file", "Actual inventory contains an undeclared file.");
    }
    return issues.length === 0 ? { ok: true, envelope: value, issues: [] } : { ok: false, issues };
}
/** Verifies the two canonical digests after strict structural/file closure validation. */
export async function verifyPigmentPublicationEnvelope(value, actualFiles) {
    const structural = validatePigmentPublicationEnvelope(value, actualFiles);
    if (!structural.ok)
        return structural;
    const documentHash = await sha256CanonicalPigmentJson(structural.envelope.body.document.value);
    if (documentHash !== structural.envelope.body.document.sha256)
        return { ok: false, issues: [{ path: "$.body.document.sha256", code: "mismatch", message: "Document digest does not match the exact portable document." }] };
    const contentHash = await sha256CanonicalPigmentJson(pigmentPublicationContent(structural.envelope.body));
    const envelopeHash = await sha256CanonicalPigmentJson({ schema: structural.envelope.schema, body: structural.envelope.body, contentHash });
    if (contentHash !== structural.envelope.contentHash || envelopeHash !== structural.envelope.envelopeHash)
        return { ok: false, issues: [{ path: "$", code: "mismatch", message: "Publication canonical hash does not match its sealed envelope." }] };
    return structural;
}
/** Seals a prevalidated body; filesystem bytes are intentionally supplied separately as descriptors. */
export async function createPigmentPublicationEnvelope(body, actualFiles) {
    const documentHash = await sha256CanonicalPigmentJson(body.document.value);
    if (body.document.sha256 !== documentHash)
        throw new TypeError("Document SHA-256 must equal the canonical portable document hash.");
    const contentHash = await sha256CanonicalPigmentJson(pigmentPublicationContent(body));
    if (body.publication.id !== `pub_${contentHash.slice("sha256:".length)}`)
        throw new TypeError("Publication ID must equal pub_ plus the canonical content hash.");
    const provisional = { schema: PIGMENT_PUBLICATION_ENVELOPE_SCHEMA, body, contentHash, envelopeHash: "sha256:" + "0".repeat(64) };
    const structural = validatePigmentPublicationEnvelope(provisional, actualFiles);
    if (!structural.ok)
        throw new TypeError(`Invalid Pigment publication envelope: ${structural.issues.map((entry) => `${entry.path}: ${entry.message}`).join(" ")}`);
    const envelopeHash = await sha256CanonicalPigmentJson({ schema: PIGMENT_PUBLICATION_ENVELOPE_SCHEMA, body, contentHash });
    return { schema: PIGMENT_PUBLICATION_ENVELOPE_SCHEMA, body, contentHash, envelopeHash };
}
